/**
 * Service worker registration with a working update flow.
 *
 * Before this module existed the SW was registered by vite-plugin-pwa's
 * injected fallback script, which calls register() once and nothing else.
 * That left two holes, observed as stale bundles surviving multiple
 * reloads across three consecutive deploys:
 *
 *  1. No reload when a new SW activates. `registerType: 'autoUpdate'`
 *     only auto-reloads open tabs when the app imports
 *     `virtual:pwa-register` — the reload lives in the virtual module's
 *     `activated` (isUpdate) handler, not in the SW itself.
 *  2. No update checks after the page has loaded. The browser runs a soft
 *     update on every full-page navigation — it refetches sw.js ignoring
 *     cache headers, and it completes before onRegisteredSW resolves, so the
 *     load itself is always covered. But this is an SPA: once it has booted,
 *     routing is client-side and no navigation ever happens again. A tab left
 *     open, and the installed PWA which never navigates at all, therefore
 *     stop checking entirely and keep serving the deploy they booted on.
 *
 * registerSW({ immediate: true }) closes (1). The interval and the foreground
 * check close (2) — nothing is needed at registration time, because the
 * navigation soft update has just run. The reload on activation can drop
 * in-progress UI state when a deploy lands, but the alternative — users
 * silently running a dead bundle whose lazy chunks 404 — is strictly worse,
 * and handleChunkReload.ts already reloads in that case anyway. The exception
 * is private mode, whose messages exist only in client memory: the checker
 * skips while it is active so the app never provokes that reload itself.
 */
import { registerSW } from 'virtual:pwa-register';

import { createSwUpdateChecker } from '@/lib/swUpdateChecker';
import { useChatStore } from '@/stores/chatStore';

/**
 * How often a tab that never leaves the foreground re-checks.
 *
 * A tab the user is actively looking at fires no `visibilitychange`, so this
 * interval is its only signal. 15 minutes bounds how long such a tab can sit
 * on a superseded bundle; at the hour this started on, "it served me a stale
 * build" was a routine report.
 */
export const UPDATE_INTERVAL_MS = 15 * 60 * 1000;

/** Breadcrumb key. Distinct from handleChunkReload.ts's reload cooldown. */
const TAKEOVER_KEY = 'andi:sw-takeover-at';

/**
 * Record the moment a new build takes over, and report it after the reload.
 *
 * The reload lives inside `virtual:pwa-register`, which fires it from
 * workbox's `activated` event; `clients.claim()` can resolve on either side
 * of that, so a `console.info` written here is usually wiped by the
 * navigation before anyone reads it. Leaving a sessionStorage breadcrumb and
 * logging it on the next boot is what makes the takeover actually observable.
 * A `controllerchange` with no previous controller is the first-ever SW
 * claiming an uncontrolled page (`clientsClaim`); nothing reloads then, so
 * stay quiet.
 */
function logBuildTakeovers(): void {
  try {
    const at = sessionStorage.getItem(TAKEOVER_KEY);
    if (at !== null) {
      sessionStorage.removeItem(TAKEOVER_KEY);
      console.info('[sw] reloaded onto a new build that took control at', at);
    }
  } catch {
    // sessionStorage unavailable (private mode, etc.) — best effort.
  }

  const container = navigator.serviceWorker;
  let hadController = container.controller !== null;
  container.addEventListener('controllerchange', () => {
    if (hadController) {
      try {
        sessionStorage.setItem(TAKEOVER_KEY, new Date().toISOString());
      } catch {
        // Best effort — the log is diagnostics, never load-bearing.
      }
    }
    hadController = true;
  });
}

export function installServiceWorker(): void {
  if (!('serviceWorker' in navigator)) return;

  logBuildTakeovers();

  registerSW({
    immediate: true,
    onRegisteredSW(swUrl, registration) {
      if (!registration) return;
      const check = createSwUpdateChecker(swUrl, registration, {
        shouldSkip: () => useChatStore.getState().privateMode,
      });
      setInterval(() => void check(), UPDATE_INTERVAL_MS);
      // The installed PWA (and backgrounded tabs) get their update check
      // when the user brings the app back to the foreground — this is the
      // path that un-pins the Android home-screen app after a deploy, and
      // for a tab the user flips between it is far quicker than the interval.
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') void check();
      });
    },
    onRegisterError(error) {
      // Without this, a failed registration (e.g. the lazy workbox-window
      // chunk failing to load) is silent and push setup stalls undiagnosably.
      console.error('[sw] service worker registration failed', error);
    },
  });
}
